import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { useTranslationMipMessages } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Text, Flex, Skeleton } from '@mantine/core';
import { SessionDetailsListItem } from '@dealstack/frontend/app/pages/Project/MIP/Messages/components/SessionDetailsListItem/SessionDetailsListItem';
import {
	DATE_FORMATS,
	formatStringDateToUiWithFallback,
} from '@dealstack/shared/lib/format/dates/date';
import type { MantineColor } from '@mantine/core/lib/core/MantineProvider';
import { StyleProp } from '@mantine/core/lib/core/Box/style-props/style-props.types';

interface Props {
	fullName: string;
	email: string;
	startDateAndTime: string;
	ipAddress: string;
	location?: string | null;
	deviceInfo: string;
	isLoading: boolean;
	sessionTitle?: string;
	mode?: 'full' | 'simple';
	bgColor?: StyleProp<MantineColor>;
}

export const SessionDetails = ({
	fullName,
	email,
	startDateAndTime,
	ipAddress,
	location,
	deviceInfo,
	isLoading,
	sessionTitle,
	mode = 'full',
	bgColor,
}: Props) => {
	const tMipMesssages = useTranslationMipMessages();

	const startedAt = formatStringDateToUiWithFallback(
		startDateAndTime,
		DATE_FORMATS.DATE_TIME_ALT
	);
	const modeBg = mode === 'full' ? 'gray.0' : 'white.0';
	const bg = bgColor || modeBg;

	return (
		<Flex direction="column" bg={bg} p={mode === 'full' ? '1rem' : 0} gap="1rem" w="100%">
			{mode === 'full' && (
				<Flex direction="column" gap="0.25rem">
					<Text c="gray.9">{sessionTitle || tMipMesssages('sentFrom')}</Text>
					{isLoading ? (
						<Skeleton height={20} />
					) : (
						<Text style={{ wordBreak: 'break-word' }}>
							{fullName} {`(${email})`}
						</Text>
					)}
				</Flex>
			)}

			<Flex direction="column" gap="0.5rem">
				<Flex align="center" gap="0.5rem">
					<Icon size="1rem" variant="monitor-02" color="gray.9" />

					<Text c="gray.9">{tMipMesssages('sessionDetails')}</Text>
				</Flex>

				<Flex direction="column" gap="0.5rem" mt="-0.125rem">
					<SessionDetailsListItem
						isLoading={isLoading}
						text={`${tMipMesssages('start')}: ${startedAt}`}
					/>

					<SessionDetailsListItem isLoading={isLoading} text={ipAddress} />

					{location && <SessionDetailsListItem isLoading={isLoading} text={location} />}

					<SessionDetailsListItem isLoading={isLoading} text={deviceInfo} />
				</Flex>
			</Flex>
		</Flex>
	);
};
